import { isRecord, list, plainText } from './payload';
import type { ChatChart, ChatChartSeries, ChatChartType } from './types';

const CHART_TYPES: ChatChartType[] = ['bar', 'line', 'pie'];

const CHART_LIMITS = {
  titleChars: 80,
  labelChars: 40,
  unitChars: 16,
  minCategories: 2,
  maxCategories: 24,
  maxPieCategories: 8,
  maxSeries: 4
} as const;

const series = (
  value: unknown,
  index: number,
  categoryCount: number
): ChatChartSeries => {
  if (!isRecord(value))
    throw new Error(`chart series[${index}] is not an object`);
  const values = list(
    value.values,
    `series[${index}].values`,
    categoryCount,
    categoryCount
  ).map((item) => {
    if (typeof item !== 'number' || !Number.isFinite(item)) {
      throw new Error(
        `chart series[${index}] has a value that is not a number`
      );
    }
    return item;
  });
  return {
    name: plainText(
      value.name,
      `series[${index}].name`,
      CHART_LIMITS.labelChars
    ),
    values
  };
};

export const parseChatChart = (payload: unknown): ChatChart => {
  if (!isRecord(payload)) throw new Error('chart payload is not an object');

  const type = payload.type;
  if (!CHART_TYPES.includes(type as ChatChartType)) {
    throw new Error('chart type is unknown');
  }
  const chartType = type as ChatChartType;

  const maxCategories =
    chartType === 'pie'
      ? CHART_LIMITS.maxPieCategories
      : CHART_LIMITS.maxCategories;
  const categories = list(
    payload.categories,
    'categories',
    CHART_LIMITS.minCategories,
    maxCategories
  ).map((item, index) =>
    plainText(item, `categories[${index}]`, CHART_LIMITS.labelChars)
  );

  const maxSeries = chartType === 'pie' ? 1 : CHART_LIMITS.maxSeries;
  const chartSeries = list(payload.series, 'series', 1, maxSeries).map(
    (item, index) => series(item, index, categories.length)
  );
  if (
    chartType === 'pie' &&
    chartSeries.some(
      ({ values }) =>
        values.some((value) => value < 0) || !values.some((value) => value > 0)
    )
  ) {
    throw new Error(
      'chart pie values must be non-negative with one above zero'
    );
  }

  const chart: ChatChart = {
    id: plainText(payload.id, 'id', CHART_LIMITS.titleChars),
    type: chartType,
    title: plainText(payload.title, 'title', CHART_LIMITS.titleChars),
    categories,
    series: chartSeries
  };
  if (payload.unit !== undefined && payload.unit !== null) {
    chart.unit = plainText(payload.unit, 'unit', CHART_LIMITS.unitChars);
  }
  if (chartType === 'bar' && payload.stacked === true) chart.stacked = true;

  return chart;
};
